import { useCallback, useContext, useEffect, useState } from "react";

import { Box, Typography } from "@mui/material";
import CircularProgressWithRatio from "../../../../UIElements/CircularProgressWithRatio/CircularProgressWithRatio";
import CustomAlert from "../../../../UIElements/CustomAlert";
import CommitmentRequirementsList from "./CommitmentRequirements/CommitmentRequirementsList";
import { LayoutBarContext } from "../../../../../contexts";
import BackButton from "../../../../UIElements/BackButton";
import Dossier, { DossierInfosType } from "../../Dossier";
import { getCommitment } from "../../../../../services";
import {
    CommitementDetails,
    CommitmentRequirementDetails,
} from "../../../../../types";
import { useCommitment } from "./useCommitment";
import { getColorByPercent } from "../../utils";
import { scrollToTop } from "../../../../../helpers";
import { useLocalStorage } from "../../../../../hooks";
import { Session } from "../../../../../types";
import {
    AcceptedFileFormatList,
    sessionStorageKey,
} from "../../../../../consts";
/**
 *
 * input : dossierId, commitmentId, referentielId
 * faire appelle au serveur pour obtenir les documments
 * dans localStorage : session : {user: {roleId, establishementId, ...infosUser(phoneNumber,..Ect)},token:"",dossiers : {currentDossier {id, commitmentId,referentielId,} }
 */

type CommitementProps = {
    commitmentId: number | null;
    dossierInfos: DossierInfosType;
};

export default function Commitment({
    commitmentId,
    dossierInfos,
}: CommitementProps) {
    const { updateContent } = useContext(LayoutBarContext);

    const { id: dossierId } = dossierInfos;

    const back = () => {
        if (dossierId) {
            updateContent(<Dossier dossierId={dossierId} />);
        }
    };

    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        auth: { user: currentUser },
    } = session;

    const userRoleId = currentUser.groups[0];

    const [commitementDetails, setCommitementDetails] =
        useState<CommitementDetails | null>(null);

    const { ratio, circualProgressRatio } =
        useCommitment(commitementDetails, userRoleId) || {};

    const fetchCommitmentDetail = useCallback(async () => {
        if (commitmentId && dossierId) {
            const commitmentDetailRes = await getCommitment(
                commitmentId,
                dossierId
            );
            if (!commitmentDetailRes.detail) {
                if (dossierInfos.establishmentType) {
                    const commitmentRequirementsFilteredByEstablishementType =
                        commitmentDetailRes.commitmentRequirements.filter(
                            (comReq: CommitmentRequirementDetails) =>
                                comReq.establishmentType ===
                                Number(dossierInfos.establishmentType)
                        );
                    setCommitementDetails({
                        ...commitmentDetailRes,
                        commitmentRequirements:
                            commitmentRequirementsFilteredByEstablishementType,
                    });
                }
            }
        }
    }, []);

    useEffect(() => {
        fetchCommitmentDetail();
        scrollToTop();
    }, [fetchCommitmentDetail]);

    return (
        <>
            {commitementDetails ? (
                <Box
                    sx={{
                        margin: 0,
                        marginLeft: "3rem",
                        marginTop: "3rem",
                        marginRight: "3rem",
                        overflowX: "hidden",
                        "@media (max-width:1024px)": {
                            marginBottom: "4.5rem",
                        },
                        // width: isMobile ? "21.375rem" : "100%",
                    }}
                >
                    <Box sx={{ marginBottom: "2rem" }}>
                        {/* header */}
                        <BackButton back={back} />

                        {ratio ? (
                            <CircularProgressWithRatio
                                boxProps={{
                                    sx: {
                                        float: "right",
                                    },
                                }}
                                value={
                                    !circualProgressRatio ||
                                    isNaN(circualProgressRatio)
                                        ? 0
                                        : circualProgressRatio
                                }
                                ratio={ratio}
                                circularProgressProps={{
                                    circualcolor: circualProgressRatio
                                        ? getColorByPercent(
                                              circualProgressRatio
                                          )
                                        : "#F43B00",
                                }}
                            />
                        ) : null}
                    </Box>

                    {/* Body */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "1rem",
                            marginTop: "0.25rem",
                            // width: isMobile ? "21.375rem" : "100%",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1.5rem",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "0.25rem",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "0.25rem",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: "#005499",
                                            fontFamily: "Roboto",
                                            fontSize: "1.75rem",
                                            fontStyle: "normal",
                                            fontWeight: 500,
                                            lineHeight: "normal",
                                        }}
                                    >
                                        {`${commitementDetails.position} ${commitementDetails.name}`}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <CustomAlert
                            variant="filled"
                            severity="info"
                            label={`Les documents à fournir ne doivent pas dépasser 25 Mo par fichier et être dans l’un des formats suivants : ${AcceptedFileFormatList}`}
                            sx={{
                                display: "flex",
                                padding: "1rem",
                                alignItems: "center",
                                gap: "2rem",
                                borderRadius: "0.75rem",
                                border: "1px solid #0082DE",
                                ".MuiAlert-icon": {
                                    width: "1.25rem",
                                    height: "1.25rem",
                                    color: "#0082DE",
                                },
                                ".MuiAlert-message": {
                                    fex: "1 0 0",
                                    color: "#023",
                                    fontFamily: "Roboto",
                                    fontSize: "0.875rem",
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    lineHeight: "120%",
                                },
                            }}
                        />
                    </Box>
                    <Box
                        sx={{
                            marginTop: "4rem",
                        }}
                    >
                        <CommitmentRequirementsList
                            commitmentRequirements={
                                commitementDetails.commitmentRequirements
                            }
                            updateCommitementDetails={fetchCommitmentDetail}
                            dossierInfos={dossierInfos}
                        />
                    </Box>
                </Box>
            ) : null}
        </>
    );
}
